
import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface AnimatedTextProps {
  text: string;
  className?: string;
  delay?: number;
  tag?: keyof JSX.IntrinsicElements;
  once?: boolean;
}

const AnimatedText = ({
  text,
  className,
  delay = 0,
  tag = "div",
  once = true,
}: AnimatedTextProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const spans = entry.target.querySelectorAll("span");
            spans.forEach((span, i) => {
              setTimeout(() => {
                span.style.opacity = "1";
                span.style.transform = "translateY(0)";
              }, delay + i * 60);
            });

            if (once) {
              observer.unobserve(entry.target);
            }
          } else if (!once) {
            const spans = entry.target.querySelectorAll("span");
            spans.forEach((span) => {
              span.style.opacity = "0";
              span.style.transform = "translateY(100%)";
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [delay, once]);

  // Create the component with the specified tag
  const Component = tag as React.ElementType;

  return (
    <Component
      className={cn("reveal-text", className)}
      ref={ref}
      aria-label={text}
    >
      <span
        className="block transition-all duration-500 ease-out"
        style={{
          opacity: 0,
          transform: "translateY(100%)",
          transitionDelay: `${delay}ms`,
        }}
      >
        {text}
      </span>
    </Component>
  );
};

export default AnimatedText;
